import React, { useState } from "react";
import { Card, Box, Modal, IconButton, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const ImageCarouselWithModal = ({
  images,
  autoSlide = true,
  slideInterval = 5000,
}) => {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useMediaQuery("(max-width:600px)");

  const handleOpen = (index) => {
    setCurrentIndex(index);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: autoSlide,
    autoplaySpeed: slideInterval,
    pauseOnHover: true,
    beforeChange: (current, next) => setCurrentIndex(next),
    arrows: !isMobile,
    appendDots: (dots) => (
      <div
        style={{
          position: "absolute",
          bottom: isMobile ? "-20px" : "-25px",
          width: "100%",
        }}
      >
        <ul
          style={{
            margin: 0,
            padding: 0,
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {dots}
        </ul>
      </div>
    ),
    customPaging: (i) => (
      <div
        style={{
          width: isMobile ? "8px" : "12px",
          height: isMobile ? "8px" : "12px",
          borderRadius: "50%",
          backgroundColor: i === currentIndex ? "#3f51b5" : "#ccc",
          margin: isMobile ? "0 4px" : "0 6px",
          transition: "all 0.3s ease",
        }}
      ></div>
    ),
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: 500,
          position: "relative",
          paddingBottom: isMobile ? "30px" : "40px",
        }}
      >
        <Slider {...settings}>
          {images.map((img, index) => (
            <div key={index}>
              <Card
                onClick={() => handleOpen(index)}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  width: "100%",
                  aspectRatio: "3/4",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    border: "2px solid transparent",
                    borderRadius: "inherit",
                    background:
                      "linear-gradient(90deg, #3f51b5, #2196f3, #3f51b5) border-box",
                    WebkitMask:
                      "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "destination-out",
                    maskComposite: "exclude",
                    animation: "borderAnimation 3s linear infinite",
                  },
                  transform: "perspective(1000px)",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  "&:hover": {
                    cursor: "pointer",
                    "&::before": {
                      animation: "borderAnimation 1.5s linear infinite",
                    },
                    "& img": {
                      transform: "scale(1.01)",
                    },
                  },
                }}
              >
                <Box
                  component="img"
                  src={img}
                  alt={`Gallery item ${index + 1}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.5s ease",
                    padding: "4px",
                  }}
                />
              </Card>
            </div>
          ))}
        </Slider>
      </Box>

      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(4px)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 1,
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "grey.500",
              bgcolor: "background.paper",
              "&:hover": {
                bgcolor: "grey.200",
              },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={images[currentIndex]}
            alt="Featured content enlarged"
            sx={{
              width: "100%",
              height: "auto",
              maxWidth: "100%",
              display: "block",
            }}
          />
        </Box>
      </Modal>

      <style jsx global>{`
        @keyframes borderAnimation {
          0% {
            background-position: 0% 50%;
          }
          100% {
            background-position: 200% 50%;
          }
        }

        /* Fix for slick carousel arrows */
        .slick-prev,
        .slick-next {
          width: 30px;
          height: 30px;
          z-index: 1;
        }
        .slick-prev:before,
        .slick-next:before {
          font-size: 30px;
          color: #3f51b5;
        }
        .slick-prev {
          left: -35px;
        }
        .slick-next {
          right: -35px;
        }

        @media (max-width: 600px) {
          .slick-prev,
          .slick-next {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default ImageCarouselWithModal;
